import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { RocketLaunch as RocketLaunchIcon } from "../icons";
import { Star as StarIcon } from "../icons";
import { functions } from "../firebase";
import { T } from "../routes/Translate";
import PaypalButton from "./PaypalButton";
import { brandColors } from "../theme";
import { FEATURE_PACKAGES, featuredUntilDate, isFeatured } from "../utils/featuredListings";
import { isIOSApp } from "../utils/device";

// Botón "Destacar" en las tarjetas de Mis autos / Mis propiedades: elige un
// paquete de días, paga a Notiline con PayPal y el servidor verifica el pago
// y marca la publicación (ver featureListing en functions/payments.js).
const FeatureListingPurchase = ({ collection, listing }) => {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(FEATURE_PACKAGES[0].days);
  const [status, setStatus] = useState(null); // "success" | "unsaved"
  const pkg = FEATURE_PACKAGES.find((p) => p.days === days);
  const featured = isFeatured(listing);
  const until = featuredUntilDate(listing);

  const handleApprove = async (details) => {
    try {
      await functions.httpsCallable("recordPayPalPayment")({
        kind: "featureListing",
        targetId: listing.id,
        orderId: details.id,
        extra: { collection, days },
      });
      setStatus("success");
    } catch (error) {
      console.error("Error al registrar el destacado:", error);
      setStatus("unsaved");
    }
  };

  const close = () => {
    setOpen(false);
    setStatus(null);
  };

  return (
    <>
      <Box sx={{ px: 2, pb: 1 }}>
        {featured && (
          <Typography sx={{ fontSize: 12, color: brandColors.accent, fontWeight: 600, mb: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
            <StarIcon sx={{ fontSize: 16 }} /> <T>Destacado hasta el</T> {until.toLocaleDateString()}
          </Typography>
        )}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<RocketLaunchIcon />}
          onClick={() => setOpen(true)}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {featured ? <T>Extender destacado</T> : <T>Destacar publicación</T>}
        </Button>
      </Box>

      <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
        <DialogTitle><T>Destacar publicación</T></DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <T>Tu publicación aparece primero en el listado y con la etiqueta "Destacado".</T>
          </Typography>
          {status ? (
            <Alert severity={status === "success" ? "success" : "warning"}>
              {status === "success" ? (
                <T>¡Listo! Tu publicación ya está destacada.</T>
              ) : (
                <T>El pago se realizó pero no se pudo aplicar. Escribinos a soporte con el comprobante de PayPal.</T>
              )}
            </Alert>
          ) : (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                {FEATURE_PACKAGES.map((p) => {
                  const selected = p.days === days;
                  return (
                    <Box
                      key={p.days}
                      component="button"
                      type="button"
                      onClick={() => setDays(p.days)}
                      sx={{
                        flex: 1,
                        p: 1.5,
                        borderRadius: 3,
                        cursor: "pointer",
                        textAlign: "center",
                        bgcolor: "transparent",
                        color: "text.primary",
                        border: "2px solid",
                        borderColor: selected ? brandColors.accent : "divider",
                        font: "inherit",
                      }}
                    >
                      <Typography sx={{ fontWeight: 700 }}>{p.days} <T>días</T></Typography>
                      <Typography sx={{ fontSize: 14, color: "text.secondary" }}>USD {p.price.toFixed(2)}</Typography>
                    </Box>
                  );
                })}
              </Stack>
              <PaypalButton
                amount={pkg.price}
                currency="USD"
                description={`Destacar publicación ${days} días`}
                clientId={process.env.REACT_APP_PAYPAL_CLIENT_ID}
                payeeEmail={process.env.REACT_APP_PLATFORM_PAYPAL_EMAIL}
                metadata={{ customId: listing.id }}
                onApprove={handleApprove}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{status ? <T>Cerrar</T> : <T>Cancelar</T>}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// En la app de iOS no se vende "Destacar" (ver isIOSApp).
const FeatureListingButton = (props) => (isIOSApp() ? null : <FeatureListingPurchase {...props} />);

export default FeatureListingButton;
